import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Alert, Typography } from "antd";
import { APIProvider, Map, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import { makeCancelable } from './lib/CancelablePromise';
import AUTH from "../../../../constants/Auth";
import { BoundsRegistryProvider } from './lib/BoundsRegistry';
export { AdvancedMarker } from './components/AdvancedMarker';
export { MarkerCluster } from './components/MarkerCluster';
export { InfoWindow } from './components/InfoWindow';
export { Rectangle } from './components/Rectangle';
export { Circle } from './components/Circle';
export { Polyline } from './components/Polyline';
export { Polygon } from './components/Polygon';
const MapStatusOverlay = ({ loadError }) => {
    const loaded = useApiIsLoaded();
    return (_jsxs(_Fragment, { children: [!loaded && _jsxs(Typography.Title, { className: "text-center", children: [_jsx(LoadingOutlined, { spin: true }), " Loading..."] }), loadError &&
                _jsx(Alert, { title: "Map Load Error", description: "The map has failed to load.  Please check your network connection and refresh this page to try again.", type: "error", showIcon: true })] }));
};
const MapReadyReporter = ({ onLoad }) => {
    const map = useMap();
    useEffect(() => {
        if (map) {
            onLoad && onLoad(map);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);
    return null;
};
const TrafficLayerToggle = ({ show }) => {
    const map = useMap();
    useEffect(() => {
        if (!map || !show) {
            return;
        }
        const trafficLayer = new google.maps.TrafficLayer();
        trafficLayer.setMap(map);
        return () => trafficLayer.setMap(null);
    }, [map, show]);
    return null;
};
const ResizeOnVisible = ({ visible }) => {
    const map = useMap();
    const prevVisible = useRef(visible);
    useEffect(() => {
        if (map && prevVisible.current !== visible) {
            google.maps.event.trigger(map, 'resize');
        }
        prevVisible.current = visible;
    }, [map, visible]);
    return null;
};
const CurrentLocationCenterer = ({ enabled, onFailed, onLoadingChange }) => {
    const map = useMap();
    useEffect(() => {
        if (!map || !enabled || !navigator?.geolocation) {
            return;
        }
        onLoadingChange(true);
        const geoPromise = makeCancelable(new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { maximumAge: 15000, timeout: 5000, enableHighAccuracy: false });
        }));
        geoPromise.promise.then((pos) => {
            onLoadingChange(false);
            map.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        }).catch((e) => {
            onLoadingChange(false);
            onFailed && onFailed(e);
        });
        return () => geoPromise.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, enabled]);
    return null;
};
const GoogleMap = (props) => {
    const [loadError, setLoadError] = useState(false);
    const [loadingLocation, setLoadingLocation] = useState(false);
    const onApiError = useCallback(() => setLoadError(true), []);
    return (_jsx("div", { id: "google-map-container", className: props.className, children: _jsxs(APIProvider, { apiKey: AUTH.googleMapsApiKey, libraries: ['places', 'marker'], onError: onApiError, children: [_jsx(MapStatusOverlay, { loadError: loadError }), loadingLocation && _jsx("div", { id: "loading-location", children: _jsx("h2", { children: _jsxs("span", { children: [_jsx(LoadingOutlined, {}), " Finding your location"] }) }) }), _jsxs(Map, { className: "google-map-node", mapId: "DEMO_MAP_ID", mapTypeControl: props.mapTypeControl ?? true, gestureHandling: props.gestureHandling ?? 'cooperative', defaultZoom: props.zoom ?? 14, ...(props.center !== undefined
                        ? { center: props.center }
                        : { defaultCenter: props.initialCenter ?? props.center }), onClick: () => props.onClick && props.onClick(), onCenterChanged: e => props.onCenterChanged && props.onCenterChanged(e.detail.center), children: [_jsx(BoundsRegistryProvider, { enabled: props.boundMapToMarkers, zoom: props.zoom, children: props.children }), _jsx(MapReadyReporter, { onLoad: props.onLoad }), _jsx(TrafficLayerToggle, { show: props.showTraffic }), _jsx(CurrentLocationCenterer, { enabled: props.centerAroundCurrentLocation, onFailed: props.onCurrentLocationFailed, onLoadingChange: setLoadingLocation }), _jsx(ResizeOnVisible, { visible: props.visible })] })] }) }));
};
export { GoogleMap };
